import * as React from "react";
import { Attach20Regular, ArrowDownload20Regular, Save20Regular, Checkmark20Regular } from "@fluentui/react-icons";
import { documentService, type DocumentDto } from "../../../services/documentService";
import { useOpenDocument } from "../../documents/useDocumentViewer";
import {
    Abschnittstitel,
    Fehlerzeile,
    Hinweis,
    Laedt,
    Suchfeld,
    Zeile,
    linkAktionStyle,
} from "./sidebarChrome";
import {
    DOK_KATEGORIE_ALTE_KUNDENAKTE,
    baueDokumentGruppen,
    type AblageEingabe,
    type AnhangEingabe,
    type DokZeile,
} from "./dokumenteGruppen";
import { INK, INK2 } from "../../chat/chatTheme";

/**
 * Reiter „Dokumente" der rechten Mail-Leiste (J6).
 *
 * Drei Gruppen an einer Stelle: die Anhänge dieser Mail (durchgereicht aus dem
 * Mailinhalt — kein zweiter Graph-Aufruf), die dauerhaft abgelegte Mail selbst
 * (Cluster ABLAGE; fehlt sie, entfällt die Gruppe) und die Dokumente des
 * zugewiesenen Kontakts.
 *
 * A12: Der Reiter ZEIGT und ÖFFNET. Der einzige Schreibweg bleibt „Beim Kontakt
 * speichern" für Anhänge dieser Mail; ein Upload bräuchte Kategorie-,
 * Vertraulichkeits- und Ordnerwahl und machte die Leiste zu einem zweiten
 * Dokumenten-Modul.
 */

const SEITE = 25;

function groesse(bytes?: number | null): string {
    if (!bytes || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function datum(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE");
}

export type DokumenteTabProps = {
    aktiv: boolean;
    contactId: number | null;
    contactName?: string | null;
    /** Anhänge der offenen Mail — durchgereicht, nicht selbst geladen. */
    anhaenge?: readonly AnhangEingabe[];
    /** Schon beim Kontakt gespeicherte Anhänge (Schlüssel `messageId:attachmentId`). */
    gespeicherteKeys?: ReadonlySet<string>;
    /** Anhang öffnen (Viewer bzw. Download). */
    onOpenAnhang?: (a: AnhangEingabe) => void;
    /** Anhang herunterladen. */
    onDownloadAnhang?: (a: AnhangEingabe) => void;
    /** Anhang beim Kontakt speichern — DER eine Schreibweg. */
    onSaveAnhang?: (a: AnhangEingabe) => void;
    /**
     * Die dauerhafte Ablage dieser Mail (Cluster ABLAGE). `null`/undefined ⇒
     * Gruppe entfällt ersatzlos, damit der Reiter auch ohne ABLAGE sinnvoll ist.
     */
    ablage?: readonly AblageEingabe[] | null;
};

export const DokumenteTab: React.FC<DokumenteTabProps> = ({
    aktiv,
    contactId,
    contactName,
    anhaenge,
    gespeicherteKeys,
    onOpenAnhang,
    onDownloadAnhang,
    onSaveAnhang,
    ablage,
}) => {
    const openDocument = useOpenDocument();
    const [suche, setSuche] = React.useState("");
    const [suchtext, setSuchtext] = React.useState("");
    const [dokumente, setDokumente] = React.useState<DocumentDto[] | null>(null);
    const [laedt, setLaedt] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);
    /** 403: der Nutzer darf diesen Kontakt nicht sehen — eine Zeile, kein Fehler. */
    const [keinKontaktZugriff, setKeinKontaktZugriff] = React.useState(false);
    /** A7 — die „Alte Kundenakte" ist standardmäßig ausgeblendet. */
    const [alteAkteZeigen, setAlteAkteZeigen] = React.useState(false);
    const [versuch, setVersuch] = React.useState(0);

    React.useEffect(() => {
        const t = window.setTimeout(() => setSuchtext(suche.trim()), 300);
        return () => window.clearTimeout(t);
    }, [suche]);

    React.useEffect(() => {
        setDokumente(null);
        setFehler(null);
        setKeinKontaktZugriff(false);
    }, [contactId, suchtext, alteAkteZeigen]);

    React.useEffect(() => {
        if (!aktiv || contactId == null || dokumente !== null) return;
        const ac = new AbortController();
        const run = async () => {
            setLaedt(true);
            setFehler(null);
            try {
                const { items } = await documentService.listPaged(
                    {
                        contactId,
                        search: suchtext || undefined,
                        limit: SEITE,
                        offset: 0,
                        excludeCategory: alteAkteZeigen ? undefined : [DOK_KATEGORIE_ALTE_KUNDENAKTE],
                    },
                    ac.signal,
                );
                setDokumente(items);
            } catch (e: any) {
                if (e?.name === "AbortError") return;
                // Das Backend setzt das Kontakt-Gate durch (J6). Ein 403 ist
                // kein Fehler der Leiste, sondern eine Aussage — als Zeile,
                // nicht als roter Kasten.
                if (String(e?.message ?? "").includes("403")) {
                    setKeinKontaktZugriff(true);
                    setDokumente([]);
                } else {
                    setFehler("Dokumente konnten nicht geladen werden.");
                }
            } finally {
                setLaedt(false);
            }
        };
        void run();
        return () => ac.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, contactId, suchtext, alteAkteZeigen, versuch]);

    const gruppen = React.useMemo(
        () =>
            baueDokumentGruppen({
                anhaenge: anhaenge ?? [],
                gespeicherteKeys: gespeicherteKeys ?? new Set<string>(),
                ablage: ablage ?? null,
                kontaktDokumente: (dokumente ?? []).map((d) => ({
                    id: d.id,
                    fileName: d.fileName,
                    fileSize: d.fileSize ?? null,
                    uploadedAt: d.uploadedAt ?? null,
                })),
                kontaktName: contactName ?? null,
                suche: suchtext,
            }),
        [anhaenge, gespeicherteKeys, ablage, dokumente, contactName, suchtext],
    );

    const anhangVon = (z: DokZeile): AnhangEingabe | null => {
        const a = (anhaenge ?? []).find((x) => x.messageId === z.messageId && x.id === z.attachmentId);
        return a ?? null;
    };

    const oeffne = (z: DokZeile) => {
        if (z.art === "anhang") {
            const a = anhangVon(z);
            if (a) onOpenAnhang?.(a);
            return;
        }
        if (z.documentId == null) return;
        openDocument(
            { id: z.documentId, fileName: z.name },
            // Nicht darstellbare Typen laden über die signierte URL herunter —
            // derselbe Weg wie in der Kundenakte.
            { download: (id) => void documentService.openSignedDownload(id) },
        );
    };

    const zustandText = (z: DokZeile): string | null => {
        if (z.art !== "ablage") return null;
        if (z.zustand === "pending") return "wird gesichert …";
        if (z.zustand === "failed") return "Sicherung fehlgeschlagen";
        if (z.zustand === "zu-gross") return "nicht dauerhaft gesichert (über 35 MB)";
        return null;
    };

    const nichtsDa =
        gruppen.length === 0 && !laedt && (contactId == null || dokumente !== null) && !fehler;

    return (
        <>
            <Suchfeld
                wert={suche}
                onWert={setSuche}
                platzhalter="Dokumente durchsuchen…"
                ariaLabel="Dokumente durchsuchen"
            />

            {fehler && (
                <Fehlerzeile
                    text={fehler}
                    onRetry={() => {
                        setFehler(null);
                        setDokumente(null);
                        setVersuch((v) => v + 1);
                    }}
                />
            )}

            {laedt && dokumente === null && contactId != null && <Laedt />}

            {gruppen.map((g) => (
                <div key={g.titel}>
                    <Abschnittstitel>{g.titel}</Abschnittstitel>
                    {g.zeilen.map((z) => {
                        const zustand = zustandText(z);
                        const klickbar = z.art === "anhang" || z.documentId != null;
                        return (
                            <Zeile key={z.key} onClick={klickbar ? () => oeffne(z) : undefined}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Attach20Regular style={{ color: INK2, flexShrink: 0 }} />
                                        <span
                                            style={{
                                                fontSize: 13,
                                                color: INK,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {z.name}
                                        </span>
                                    </span>
                                    {z.art === "anhang" && (
                                        <span
                                            style={{ display: "inline-flex", gap: 2, flexShrink: 0 }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                type="button"
                                                title="Herunterladen"
                                                aria-label={`${z.name} herunterladen`}
                                                onClick={() => {
                                                    const a = anhangVon(z);
                                                    if (a) onDownloadAnhang?.(a);
                                                }}
                                                style={{
                                                    border: "none",
                                                    background: "transparent",
                                                    cursor: "pointer",
                                                    color: INK2,
                                                    display: "inline-flex",
                                                    padding: 2,
                                                }}
                                            >
                                                <ArrowDownload20Regular />
                                            </button>
                                            <button
                                                type="button"
                                                title={
                                                    contactId == null
                                                        ? "Zuweisung ist Voraussetzung"
                                                        : z.gespeichert
                                                          ? "Beim Kontakt gespeichert"
                                                          : `Bei ${contactName ?? "dem Kontakt"} als Dokument speichern`
                                                }
                                                aria-label={`${z.name} beim Kontakt speichern`}
                                                disabled={contactId == null || z.gespeichert}
                                                onClick={() => {
                                                    const a = anhangVon(z);
                                                    if (a) onSaveAnhang?.(a);
                                                }}
                                                style={{
                                                    border: "none",
                                                    background: "transparent",
                                                    cursor:
                                                        contactId == null || z.gespeichert
                                                            ? "default"
                                                            : "pointer",
                                                    color: z.gespeichert ? "#2e7d32" : INK2,
                                                    opacity: contactId == null ? 0.5 : 1,
                                                    display: "inline-flex",
                                                    padding: 2,
                                                }}
                                            >
                                                {z.gespeichert ? <Checkmark20Regular /> : <Save20Regular />}
                                            </button>
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: "flex", gap: 10, fontSize: 12, color: INK2 }}>
                                    {groesse(z.groesse) && <span>{groesse(z.groesse)}</span>}
                                    {datum(z.datum) && <span>{datum(z.datum)}</span>}
                                    {zustand && (
                                        <span style={{ color: z.zustand === "failed" ? "#b3261e" : INK2 }}>
                                            {zustand}
                                        </span>
                                    )}
                                </div>
                            </Zeile>
                        );
                    })}
                </div>
            ))}

            {keinKontaktZugriff && (
                <div style={{ fontSize: 12, color: INK2, marginTop: 4, lineHeight: 1.4 }}>
                    Die Dokumente dieses Kontakts darfst du nicht sehen.
                </div>
            )}

            {contactId != null && !keinKontaktZugriff && (
                <button
                    type="button"
                    style={{ ...linkAktionStyle, marginTop: 8 }}
                    onClick={() => setAlteAkteZeigen((v) => !v)}
                >
                    {alteAkteZeigen ? "Alte Kundenakte ausblenden" : "Alte Kundenakte einblenden"}
                </button>
            )}

            {nichtsDa && (
                <Hinweis>
                    Keine Dokumente zu dieser Mail.
                    {contactId == null && (
                        <>
                            <br />
                            Weise die Mail einem Kontakt zu, um dessen Dokumente zu sehen.
                        </>
                    )}
                </Hinweis>
            )}
        </>
    );
};
